import React, { useState, useCallback } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { Mail, Lock, ArrowRight, ArrowLeft, Loader2, Eye, EyeOff } from 'lucide-react';
import { InvestLanguageFlags } from '../components/InvestLanguageFlags';
import { facebookEnabled, facebookLoginPopup } from '../lib/facebookAuth';
import { GoogleAuthButton } from '../components/GoogleAuthButton';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BG = 'https://images.unsplash.com/photo-1694880912546-5d9619757fbb?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTN8MHwxfHNlYXJjaHwyfHxsYXR2aWElMjBsYW5kc2NhcGUlMjBmb2d8ZW58MHx8fHwxNzg0MTcwOTc1fDA&ixlib=rb-4.1.0&q=85';

export default function LoginPage() {
  const { login, loginWithFacebook } = useAuth();
  const { t, language } = useLanguage();
  const navigate = useNavigate();
  const [sp] = useSearchParams();
  const nextPath = sp.get('next');
  const safeNext = nextPath && nextPath.startsWith('/') ? nextPath : '/';

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [needsVerification, setNeedsVerification] = useState(false);
  const [resendStatus, setResendStatus] = useState('');
  const [loading, setLoading] = useState(false);

  const isLV = language === 'lv';
  const L = (lv, en, ru) => (language === 'lv' ? lv : language === 'ru' ? (ru ?? en) : en);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setNeedsVerification(false);
    setResendStatus('');
    setLoading(true);
    const result = await login(email, password);
    if (result.success) {
      navigate(safeNext);
    } else {
      setError(result.error);
      if (result.emailNotVerified) setNeedsVerification(true);
    }
    setLoading(false);
  };

  const handleResend = async () => {
    if (!email) return;
    setResendStatus('sending');
    try { await axios.post(`${API}/auth/resend-verification`, { email: email.trim().toLowerCase() }); } catch {}
    setResendStatus('sent');
  };

  const startFacebook = useCallback(async () => {
    setError('');
    try {
      const token = await facebookLoginPopup();
      const result = await loginWithFacebook(token);
      if (result.success) {
        navigate(result.user?.onboarded ? '/' : '/welcome');
      } else {
        setError(result.error);
      }
    } catch (e) {
      if (e?.message !== 'cancelled') {
        setError(L('Neizdevās pieslēgties ar Facebook', 'Facebook sign-in failed', 'Не удалось войти через Facebook'));
      }
    }
  }, [loginWithFacebook, navigate, L]);

  return (
    <div className="fixed inset-0 overflow-y-auto" style={{ background: '#0A0D0B' }} data-testid="login-page">
      <div className="absolute inset-0 z-0">
        <img src={BG} alt="" className="w-full h-full object-cover opacity-30 scale-105" />
        <div className="absolute inset-0" style={{ background: 'radial-gradient(120% 90% at 50% 8%, rgba(10,13,11,0.15), rgba(10,13,11,0.9))' }} />
      </div>

      <div className="relative z-10 min-h-full flex items-center justify-center py-10 px-5">
      <motion.div
        initial={{ opacity: 0, y: 24, scale: 0.98 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
        className="relative z-10 w-full max-w-md rounded-[28px] border border-white/10 bg-[#0A0D0B]/70 backdrop-blur-2xl shadow-[0_8px_40px_rgba(0,0,0,0.6)] p-8 sm:p-10"
        style={{ fontFamily: 'Manrope, sans-serif' }}
      >
        <div className="flex justify-center mb-6">
          <InvestLanguageFlags />
        </div>
        <Link to="/" className="inline-flex items-center gap-1.5 text-white/50 hover:text-white text-xs font-semibold mb-5 transition-colors" data-testid="login-home-link">
          <ArrowLeft className="w-3.5 h-3.5" /> {L('Uz sākumu', 'Home', 'На главную')}
        </Link>
        <p className="text-[11px] font-bold uppercase tracking-[0.22em] text-amber-500/80 mb-3">KoLV · Baltijas kopiena</p>
        <h1 className="text-white text-4xl sm:text-5xl font-light tracking-tighter mb-2" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
          {L('Laipni lūdzam', 'Welcome back', 'Добро пожаловать')}<span className="text-amber-500">.</span>
        </h1>
        <p className="text-white/60 text-sm mb-8">{L('Pieslēdzies un atrodi savu apkaimi.', 'Sign in and find your neighborhood.', 'Войдите и найдите свой район.')}</p>

        <GoogleAuthButton
          className="w-full h-12 rounded-full bg-white text-[#0A0D0B] font-semibold text-sm flex items-center justify-center gap-3 hover:bg-white/90 transition-colors"
          testid="login-google-btn"
          onDone={(u) => navigate(u?.onboarded ? safeNext : '/welcome')}
        >
          <svg className="w-5 h-5" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23z"/><path fill="#FBBC05" d="M5.84 14.1a6.6 6.6 0 0 1 0-4.2V7.06H2.18a11 11 0 0 0 0 9.88l3.66-2.84z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1A11 11 0 0 0 2.18 7.06l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z"/></svg>
          {L('Turpināt ar Google', 'Continue with Google', 'Продолжить с Google')}
        </GoogleAuthButton>

        {facebookEnabled() && (
          <button
            onClick={startFacebook}
            className="w-full h-12 mt-3 rounded-full bg-[#1877F2] text-white font-semibold text-sm flex items-center justify-center gap-3 hover:bg-[#166fe0] transition-colors"
            data-testid="login-facebook-btn"
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor"><path d="M24 12.07C24 5.4 18.63 0 12 0S0 5.4 0 12.07C0 18.1 4.39 23.1 10.13 24v-8.44H7.08v-3.49h3.05V9.41c0-3.02 1.79-4.69 4.53-4.69 1.31 0 2.68.24 2.68.24v2.97h-1.51c-1.49 0-1.95.93-1.95 1.89v2.25h3.32l-.53 3.49h-2.79V24C19.61 23.1 24 18.1 24 12.07z"/></svg>
            {L('Turpināt ar Facebook', 'Continue with Facebook', 'Продолжить с Facebook')}
          </button>
        )}

        <div className="flex items-center gap-3 my-6">
          <div className="h-px flex-1 bg-white/10" />
          <span className="text-white/40 text-xs uppercase tracking-widest">{L('vai', 'or', 'или')}</span>
          <div className="h-px flex-1 bg-white/10" />
        </div>

        <form onSubmit={handleSubmit} className="space-y-3">
          {error && (
            <div className="p-3 rounded-2xl bg-red-500/10 border border-red-500/30 text-red-300 text-sm" data-testid="error-message">
              {error}
              {needsVerification && (
                <div className="mt-3 pt-3 border-t border-red-500/20">
                  {resendStatus === 'sent' ? (
                    <p className="text-green-300 text-xs">
                      {L('✓ Jauna vēstule nosūtīta. Pārbaudi iesūtni (un spam).', '✓ New email sent. Check your inbox (and spam).', '✓ Новое письмо отправлено. Проверьте почту (и спам).')}
                    </p>
                  ) : (
                    <button type="button" onClick={handleResend} disabled={resendStatus === 'sending'} className="text-xs font-medium text-amber-400 hover:underline" data-testid="resend-verification-link">
                      {resendStatus === 'sending'
                        ? L('Sūta...', 'Sending...', 'Отправка...')
                        : L('Nosūtīt apstiprinājuma saiti vēlreiz →', 'Resend verification link →', 'Отправить ссылку подтверждения ещё раз →')}
                    </button>
                  )}
                </div>
              )}
            </div>
          )}

          <div className="relative">
            <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              placeholder={t('email')}
              className="w-full h-12 pl-11 pr-4 rounded-2xl bg-white/5 border border-white/10 text-white placeholder-white/40 text-base focus:border-amber-500/60 focus:bg-white/[0.07] focus:outline-none transition-colors"
              data-testid="email-input"
            />
          </div>

          <div className="relative">
            <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
            <input
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              placeholder={t('password')}
              className="w-full h-12 pl-11 pr-10 rounded-2xl bg-white/5 border border-white/10 text-white placeholder-white/40 text-base focus:border-amber-500/60 focus:bg-white/[0.07] focus:outline-none transition-colors"
              data-testid="password-input"
            />
            <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3.5 top-1/2 -translate-y-1/2 text-white/40 hover:text-white transition-colors">
              {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>

          <div className="text-right">
            <Link to="/forgot-password" className="text-xs text-amber-400 hover:underline font-medium" data-testid="forgot-password-link">
              {L('Aizmirsi paroli?', 'Forgot password?', 'Забыли пароль?')}
            </Link>
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full h-12 rounded-full bg-amber-500 text-[#0A0D0B] font-bold text-sm flex items-center justify-center gap-2 hover:bg-amber-400 transition-colors disabled:opacity-60"
            style={{ boxShadow: '0 8px 30px rgba(255,159,28,0.35)' }}
            data-testid="login-submit-btn"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <>{t('login')} <ArrowRight className="w-4 h-4" /></>}
          </button>
        </form>

        <p className="text-center text-white/50 text-sm mt-6">
          {t('noAccount')}{' '}
          <Link to="/register" className="text-amber-500 font-semibold hover:underline" data-testid="register-link">
            {t('register')}
          </Link>
        </p>
      </motion.div>
      </div>
    </div>
  );
}
